import type { CampaignNegativesResponse, CampaignNegativeTarget } from "@adpilot/shared";
import {
  IconArrowDown,
  IconArrowsUpDown,
  IconArrowUp,
  IconCopy,
  IconDownload,
  IconPlus,
} from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ColumnCustomizerDialog } from "@/components/campaign/ColumnCustomizerDialog";
import { ControlButton } from "@/components/patterns/control-button";
import { DataTable, DataTableFrame } from "@/components/patterns/DataTable";
import { DataTablePagination } from "@/components/patterns/DataTablePagination";
import { FilterSelect } from "@/components/patterns/filter-select";
import { SearchInput } from "@/components/patterns/SearchInput";
import { UnavailableControl } from "@/components/patterns/unavailable-control";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { SegmentControl, SegmentControlItem } from "@/components/ui/segment-control";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { csvDocument, downloadCsv } from "../ad-groups/ad-groups-table-model";

export interface CampaignNegativesTableProps {
  campaignId: string;
  data?: CampaignNegativesResponse;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
  renderAdGroupLink?: (adGroupId: string, name: string) => ReactNode;
}

/** Reference sub tabs: 否定词 / 否定商品 (product and brand negatives). */
export const NEGATIVE_SUB_TABS = ["keyword", "product"] as const;
export type NegativeSubTab = (typeof NEGATIVE_SUB_TABS)[number];
export const negativeSubTab = (row: Pick<CampaignNegativeTarget, "targetType">): NegativeSubTab =>
  row.targetType === "keyword" ? "keyword" : "product";

const FIXED_COLUMNS = ["active", "name"] as const;
const COLUMNS = ["level", "adGroup", "amazonCreatedAt"] as const;
type Column = (typeof COLUMNS)[number];
type SortKey = Column | "name";
const ALL_COLUMNS: readonly ((typeof FIXED_COLUMNS)[number] | Column)[] = [
  ...FIXED_COLUMNS,
  ...COLUMNS,
];
const LEVEL_FILTERS = ["all", "campaign", "ad_group"] as const;
const STATUS_FILTERS = ["all", "enabled", "paused", "archived"] as const;
const PAGE_SIZES = [25, 50, 100] as const;
const SWITCH_STICKY = "sticky left-0 z-20 w-16 min-w-16 max-w-16 border-r border-line";
const NAME_STICKY =
  "sticky left-16 z-20 min-w-56 border-r border-line shadow-[4px_0_8px_-7px_rgb(15_23_42_/_0.45)]";

function sortValue(row: CampaignNegativeTarget, key: SortKey): string | null {
  switch (key) {
    case "name":
      return row.label;
    case "level":
      return row.targetLevel;
    case "adGroup":
      return row.adGroup;
    case "amazonCreatedAt":
      return row.amazonCreatedAt;
  }
}

export function CampaignNegativesTable(props: CampaignNegativesTableProps) {
  const { t, i18n } = useTranslation("campaign");
  const text = (key: string, options?: Record<string, unknown>) =>
    t(`detail.negativesList.${key}`, options);
  const shared = (key: string, options?: Record<string, unknown>) =>
    t(`detail.adGroupsList.${key}`, options);
  const [subTab, setSubTab] = useState<NegativeSubTab>("keyword");
  const [search, setSearch] = useState("");
  const [level, setLevel] = useState<(typeof LEVEL_FILTERS)[number]>("all");
  const [status, setStatus] = useState<(typeof STATUS_FILTERS)[number]>("all");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState<number>(PAGE_SIZES[0]);
  const [sort, setSort] = useState<{ key: SortKey; direction: "asc" | "desc" }>({
    key: "name",
    direction: "asc",
  });
  const [columns, setColumns] = useState<readonly Column[]>(COLUMNS);

  const all = props.data?.connected ? props.data.negatives : [];
  const inTab = all.filter((row) => negativeSubTab(row) === subTab);
  const filtered = [
    ...inTab.filter(
      (row) =>
        row.label.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()) &&
        (level === "all" || row.targetLevel === level) &&
        (status === "all" || row.status === status),
    ),
  ].sort((a, b) => {
    const x = sortValue(a, sort.key);
    const y = sortValue(b, sort.key);
    if (x === null) return y === null ? 0 : 1;
    if (y === null) return -1;
    return x.localeCompare(y) * (sort.direction === "asc" ? 1 : -1);
  });
  const pages = Math.max(1, Math.ceil(filtered.length / size));
  const currentPage = Math.min(page, pages);
  const visible = filtered.slice((currentPage - 1) * size, currentPage * size);
  const reset = () => setPage(1);
  const label = (key: SortKey | (typeof FIXED_COLUMNS)[number]): string => {
    if (key === "active") return shared("active");
    if (key === "name") return text(`columns.name.${subTab}`);
    return text(`columns.${key}`);
  };
  const formatCell = (row: CampaignNegativeTarget, key: Column): string => {
    if (key === "level") return text(`levels.${row.targetLevel}`);
    if (key === "adGroup") return row.adGroup ?? "—";
    return row.amazonCreatedAt
      ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(
          new Date(row.amazonCreatedAt),
        )
      : "—";
  };
  const sortBy = (key: SortKey) => {
    setSort({ key, direction: sort.key === key && sort.direction === "asc" ? "desc" : "asc" });
    setPage(1);
  };
  const copyLabel = async (row: CampaignNegativeTarget) => {
    try {
      await navigator.clipboard.writeText(row.label);
      toast.success(text("copied"));
    } catch {
      toast.error(text("copyFailed"));
    }
  };
  const exportCsv = () => {
    downloadCsv(
      `campaign-${props.campaignId}-negatives-${subTab}.csv`,
      csvDocument([
        (["name", ...columns] as SortKey[]).map(label),
        ...filtered.map((row) => [row.label, ...columns.map((key) => formatCell(row, key))]),
      ]),
    );
  };

  const sortHeader = (key: SortKey, className?: string) => {
    const active = sort.key === key;
    return (
      <TableHead
        key={key}
        className={className}
        aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
      >
        <button
          type="button"
          aria-label={label(key)}
          className="flex w-full min-w-0 items-center gap-1 truncate text-left uppercase outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => sortBy(key)}
        >
          <span className="truncate">{label(key)}</span>
          {active && sort.direction === "asc" ? (
            <IconArrowUp aria-hidden="true" className="size-3 shrink-0" />
          ) : active ? (
            <IconArrowDown aria-hidden="true" className="size-3 shrink-0" />
          ) : (
            <IconArrowsUpDown aria-hidden="true" className="size-3 shrink-0 opacity-45" />
          )}
        </button>
      </TableHead>
    );
  };

  const disabledControl = (labelText: string, reason: string) => (
    <UnavailableControl variant="outline" reason={reason}>
      {labelText}
    </UnavailableControl>
  );

  const table = (
    <DataTable density="compact" stickyHeader aria-label={text("title")}>
      <TableHeader>
        <TableRow>
          <TableHead className={cn(SWITCH_STICKY, "z-30")}>{shared("active")}</TableHead>
          {sortHeader("name", cn(NAME_STICKY, "z-30"))}
          {columns.map((key) => sortHeader(key))}
          <TableHead className="text-right">{text("columns.action")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {visible.map((row) => (
          <TableRow key={row.id} className="hover:bg-table-row-hover">
            <TableCell className={cn(SWITCH_STICKY, "bg-surface")} title={text("writeUnavailable")}>
              <Switch
                aria-label={`${shared("active")} ${row.label}`}
                checked={row.status === "enabled"}
                disabled
              />
            </TableCell>
            <TableCell className={cn(NAME_STICKY, "bg-surface")}>
              <span className="flex items-center gap-1.5">
                {row.keywordMatchType && (
                  <Badge
                    variant="neutral"
                    size="compact"
                    className="font-normal"
                    title={text(`matchTypes.${row.keywordMatchType}`)}
                  >
                    {text(`matchTypeShort.${row.keywordMatchType}`)}
                  </Badge>
                )}
                <span
                  className="truncate text-[11px] font-semibold leading-4 text-ink"
                  title={row.label}
                >
                  {row.label}
                </span>
              </span>
            </TableCell>
            {columns.map((key) => (
              <TableCell key={key}>
                {key === "adGroup" && row.adGroupId && row.adGroup ? (
                  props.renderAdGroupLink ? (
                    props.renderAdGroupLink(row.adGroupId, row.adGroup)
                  ) : (
                    <Link
                      to="/campaigns/$id/ad-groups/$adGroupId"
                      params={{ id: props.campaignId, adGroupId: row.adGroupId }}
                      className="text-ink"
                    >
                      {row.adGroup}
                    </Link>
                  )
                ) : (
                  formatCell(row, key)
                )}
              </TableCell>
            ))}
            <TableCell className="text-right">
              <ControlButton
                variant="ghost"
                controlSize="icon-row"
                aria-label={`${text("copy")} ${row.label}`}
                title={text("copy")}
                onClick={() => void copyLabel(row)}
              >
                <IconCopy />
              </ControlButton>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </DataTable>
  );

  const toolbar = (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <UnavailableControl reason={text("writeUnavailable")}>
          <IconPlus data-icon="inline-start" />
          {text(`create.${subTab}`)}
        </UnavailableControl>
        {disabledControl(text("bulk"), text("writeUnavailable"))}
        {disabledControl("ASIN", shared("productUnavailable"))}
        <FilterSelect
          ariaLabel={text("filters.level")}
          items={LEVEL_FILTERS.map((value) => ({
            value,
            label: value === "all" ? text("filters.level") : text(`levelFilter.${value}`),
          }))}
          value={level}
          onValueChange={(value) => {
            setLevel(value as (typeof LEVEL_FILTERS)[number]);
            reset();
          }}
        />
        <FilterSelect
          ariaLabel={text(`filters.active.${subTab}`)}
          items={STATUS_FILTERS.map((value) => ({
            value,
            label: value === "all" ? shared("states.all") : shared(`states.${value}`),
          }))}
          value={status}
          onValueChange={(value) => {
            setStatus(value as (typeof STATUS_FILTERS)[number]);
            reset();
          }}
        />
        {disabledControl(text("filters.createdBy"), text("provenanceUnavailable"))}
        {disabledControl(shared("conditions"), shared("conditionsUnavailable"))}
      </div>
      <div className="flex items-center gap-2">
        <SearchInput
          aria-label={shared("search")}
          placeholder={shared("search")}
          value={search}
          showIcon
          textSize="compact"
          className="w-[200px]"
          onChange={(event) => {
            setSearch(event.target.value);
            reset();
          }}
        />
        <ControlButton
          variant="outline"
          controlSize="icon-toolbar"
          aria-label={shared("export")}
          title={shared("export")}
          disabled={filtered.length === 0 || props.isLoading || props.isError}
          onClick={exportCsv}
        >
          <IconDownload />
        </ControlButton>
        <ColumnCustomizerDialog
          allColumns={ALL_COLUMNS}
          fixedColumns={FIXED_COLUMNS}
          defaultColumns={COLUMNS}
          visibleColumns={columns}
          label={(column) => label(column)}
          description={text("customColumnsDescription")}
          onApply={(next) =>
            setColumns(
              next.filter((column): column is Column => column !== "active" && column !== "name"),
            )
          }
        />
      </div>
    </>
  );

  const body = props.isLoading ? (
    <div className="flex flex-col gap-2 p-3.5" role="status" aria-label={shared("loading")}>
      {[1, 2, 3, 4].map((n) => (
        <Skeleton key={n} className="h-8 w-full" />
      ))}
    </div>
  ) : props.isError ? (
    <div className="p-3.5">
      <ErrorState
        message={text("loadFailed")}
        onRetry={props.onRetry}
        isRetrying={props.isFetching}
      />
    </div>
  ) : filtered.length === 0 ? (
    <div className="p-3.5">
      <EmptyState
        title={text("empty")}
        description={text(inTab.length ? "noMatches" : "emptyDescription")}
      />
    </div>
  ) : (
    table
  );

  return (
    <section
      aria-label={text("title")}
      className="flex flex-col gap-[14px]"
      aria-busy={props.isFetching}
    >
      <SegmentControl
        size="compact"
        aria-label={text("title")}
        value={subTab}
        onValueChange={(value) => {
          setSubTab(value);
          reset();
        }}
      >
        {NEGATIVE_SUB_TABS.map((value) => (
          <SegmentControlItem key={value} value={value}>
            {text(`subTabs.${value}`)}
            <span className="font-normal tabular-nums">
              {all.filter((row) => negativeSubTab(row) === value).length}
            </span>
          </SegmentControlItem>
        ))}
      </SegmentControl>
      <DataTableFrame
        toolbar={toolbar}
        footer={
          <DataTablePagination
            page={currentPage}
            pageCount={pages}
            pageSize={size}
            pageSizeOptions={PAGE_SIZES}
            summary={shared("count", { count: filtered.length })}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setSize(next);
              reset();
            }}
          />
        }
      >
        {body}
      </DataTableFrame>
    </section>
  );
}
